import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { useNavigate, Link } from "react-router-dom";
import "../../styles/Business.css";

import BlogList from "../../components/BlogList";

const tipsList = [
  "Ensure your solar panels face the right direction for maximum sunlight.",
  "Clean solar panels every 3–6 months to maintain efficiency.",
  "Avoid shading from trees or nearby structures.",
  "Use energy-efficient appliances to extend battery life.",
  "Install a charge controller to prevent battery overcharging.",
  "Monitor your solar output regularly using monitoring tools.",
  "Install lightning arrestors in areas prone to storms.",
  "Turn off unused devices to conserve power.",
  "Use LED bulbs to save energy.",
  "Do not mix old and new batteries in your system.",
  "Hire certified solar technicians for professional installation.",
  "Store batteries in cool, dry places.",
  "Use solar combiner boxes for organized connections.",
  "Schedule routine maintenance checks for your system.",
  "Always use the correct size of cables and fuses.",
];

export default function DashboardBusiness() {
  const { user, logout, token } = useContext(AuthContext);
  const { unreadCount } = useContext(NotificationContext);
  const [tips, setTips] = useState([]);
  const [inbox, setInbox] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const updateCoordinates = async (lat, lon) => {
      try {
        await fetch("http://localhost:5000/api/auth/update-coordinates", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ lat, lon }),
        });
      } catch (err) {
        console.error("📍 Failed to update coordinates:", err.message);
      }
    };

    const fetchCoordinates = () => {
      if (!navigator.geolocation || !user || !token) return;
      if (user?.coordinates?.lat && user?.coordinates?.lon) return;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          updateCoordinates(latitude, longitude);
        },
        (error) => {
          console.warn("📍 Geolocation error:", error.message);
        }
      );
    };

    fetchCoordinates();
  }, [user, token]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      const shuffled = [...tipsList].sort(() => 0.5 - Math.random());
      setTips(shuffled.slice(0, 10));
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchInbox = async () => {
      try {
        const res = await fetch("http://localhost:5000/api/chats/inbox", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        setInbox(data || []);
      } catch (err) {
        console.error("Inbox fetch failed:", err.message);
      }
    };

    if (token) fetchInbox();
  }, [token]);

  if (!user) return null;

  return (
    <div className="dashboard-layout">
      {/* ========== Sidebar Navigation ========== */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="brand">⚡ Globisun</h1>
        </div>
        <nav className="nav-links">
          <Link to="/calculator/load">🔌 Load Calculator</Link>
          <Link to="/calculator/panels">🔋 Panel Sizing Tool</Link>
          <Link to="/calculator/sizing">🧮 Full Solar Sizing</Link>
          <Link to="/notifications">🌤️ Solar Weather Alerts</Link>
         {/*
  <Link to="/installers">🧰 Find Installers</Link>
  <Link to="/vendors">🏪 Upload Products/Services</Link>
  <Link to="/blogs">📚 Blog & Solar Guides</Link>
  <Link to="/resources">🗂️ Downloads & Resources</Link>
  <hr />
  <Link to="/listings">🔖 Listings</Link>
  <Link to="/profiles">🧑‍🔧 Profiles</Link>
*/}

          <Link to="/bookings">📅 Bookings</Link>
          <Link to="/ai-assistant">
            💬 Chat{" "}
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </Link>
        </nav>
      </aside>

      {/* ========== Main Content ========== */}
      <main className="dashboard-content">
        <header className="dashboard-header">
          <h2>
            Welcome, {user.name} <span className="wave">👋</span>
          </h2>
          <p>Manage your solar solutions business efficiently 🌞</p>
        </header>

        {/* 🔔 Notification */}
        <div className="notification-bell">
          🔔 Notifications: {unreadCount}
        </div>

        {/* 🌞 Tips Section */}
        <section className="hero-section">
          <h3>🌞 Solar Installation & Maintenance Tips</h3>
          <div className="tips-carousel-wrapper">
            <div
              className="tips-carousel"
              style={{ animationDuration: `${tips.length * 5}s` }}
            >
              {[...tips, ...tips].map((tip, index) => (
                <div key={index} className="tip-item">
                  {index % tips.length + 1}. {tip}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 🛠️ Tool Section */}
        <section className="tool-grid">
          <div className="tool-card"><Link to="/calculator/panels">🔋 Solar Panel Sizing</Link></div>
          <div className="tool-card"><Link to="/calculator/sizing">🧮 Full Solar Sizing</Link></div>
          <div className="tool-card"><Link to="/notifications">🌤️ Solar Weather Alerts</Link></div>

          {/* New Device Error Codes tool card */}
                    <div className="tool-card">
                      <Link to="/error-codes">⚙️ Error Codes Checker</Link>
                    </div>

                    <div className="tool-card"><Link to="/service-centers">🔧 Service Center Locator</Link></div>

                    <div className="tool-card">
                               <Link to="/solar-resources">📚 Solar Resources</Link>
                              </div>
                    
          <div className="tool-card"><Link to="/installers">🧰 Find Installers</Link></div>
          <div className="tool-card"><Link to="/vendors">🏪 Upload Products & Services</Link></div>
          <div className="tool-card"><Link to="/blogs">📚 Blog & Solar Guides</Link></div>
          <div className="tool-card"><Link to="/resources">🗂️ Downloads & Resources</Link></div>
          <div className="tool-card"><Link to="/listings">🔖 Listings</Link></div>
          <div className="tool-card"><Link to="/profiles">🧑‍🔧 Profiles</Link></div>
          <div className="tool-card"><Link to="/bookings">📅 Bookings</Link></div>

          {/* 💬 Chat Preview */}
          <div className="tool-card chat-tool" onClick={() => navigate("/inbox")}>
            💬 Chat{" "}
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
            <div className="chat-preview">
              {inbox.length === 0 ? (
                <p className="no-messages">No recent messages.</p>
              ) : (
                inbox.slice(0, 2).map((conv, idx) => (
                  <div key={idx} className="preview-item">
                    <strong>{conv.participants[1]?.name || "User"}</strong>:{" "}
                    {conv.latestMessage?.message?.slice(0, 30)}...
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

         <BlogList />       
        {/* 🚪 Logout */}
        <div className="bottom-logout">
          <button className="logout-btn" onClick={logout}>
            🚪 Logout
          </button>
        </div>
      </main>
    </div>
  );
}
